import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import TextField from '@material-ui/core/TextField';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import Dialog from '@serko/zui/es/Dialog';
import DialogCloseButton from '@serko/zui/es/DialogCloseButton';
import DialogTitle from '@serko/zui/es/DialogTitle';
import Notification from '@serko/zui/es/Notification';
import NotificationContentText from '@serko/zui/es/NotificationContentText';
import React, { ChangeEvent, Dispatch, PureComponent } from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import pure from 'recompose/pure';
import { Action } from 'redux';
import messages from '../intl/messages';
import { ItineraryStatus } from '../models/itinerary';
import { AppState } from '../store/model';
import {
  toggleConfirmDialog,
  updateUserNote,
} from '../store/reducers/confirm-dialog/actions';
import {
  requestItinerary,
  requestItineraryChanges,
} from '../store/reducers/itinerary/actions';
import {
  getTravelArrangerName,
  getTravelArrangerNotes,
} from '../store/reducers/itinerary/selectors';
import { getTextforPopup } from '../utils/confirm-dialog-text';

interface OwnProps {
  fullScreen?: boolean;
}

interface StateProps {
  open: boolean;
  userNote: string;
  itineraryStatus: ItineraryStatus;
  loading: boolean;
  complete: boolean;
  error: boolean;
  arrangerNote: string;
  arrangerName: string;
}

interface DispatchProps {
  handleClose(itineraryStatus: ItineraryStatus, actionComplete: boolean): void;
  setUserNote(note: string): void;
  handleAccept(): void;
}
interface ConfirmDialogProps
  extends OwnProps,
    StateProps,
    DispatchProps,
    WithStyles<typeof styles>,
    InjectedIntlProps {}

class ConfirmDialogBase extends PureComponent<ConfirmDialogProps> {

  public render() {
    const {
      fullScreen,
      itineraryStatus,
      arrangerName,
      arrangerNote,
      open,
      error,
      complete,
      classes,
      userNote,
      loading,
      intl: { formatMessage },
    } = this.props;

    const {
      title,
      emailSent,
      emailSentMessage,
      message,
      noteHeading,
      viewItinerary,
      accept,
      input,
    } = getTextforPopup(itineraryStatus, arrangerName);

    return (
      <Dialog fullScreen={fullScreen} open={open} onClose={this.handleClose}>
        <DialogTitle>
          {title}
          <DialogCloseButton onClick={this.handleClose} />
        </DialogTitle>
        <DialogContent>
          {complete ? (
            <Notification type="Success" title={emailSent}>
              <NotificationContentText>
                {emailSentMessage}
              </NotificationContentText>
            </Notification>
          ) : (
            <>
              {error && (
                <Notification type="Error">
                  <NotificationContentText>
                    {formatMessage(messages.common.errorOccurred)}
                  </NotificationContentText>
                </Notification>
              )}
              <DialogContentText paragraph>{message}</DialogContentText>
              {itineraryStatus !== 'Rejected' && !!arrangerNote && (
                <>
                  <DialogContentText paragraph>{noteHeading}</DialogContentText>
                  <DialogContentText
                    color="textSecondary"
                    className={classes.italic}
                    paragraph
                  >
                    {arrangerNote}
                  </DialogContentText>
                </>
              )}
              <TextField
                margin="normal"
                id="name"
                label={input}
                type="text"
                fullWidth
                required={itineraryStatus === 'Rejected'}
                multiline
                value={userNote}
                onChange={this.handleChangeNote}
                disabled={loading}
                className={classes.textInput}
                helperText={formatMessage(
                  messages.notification.messageHelperText,
                )}
              />
              {itineraryStatus !== 'Rejected' && (
                <Notification
                  type="Info"
                  title={formatMessage(messages.notification.info.title)}
                >
                  <NotificationContentText component="span">
                    <ul>
                      <li>
                        {formatMessage(messages.notification.info.bullet1)}
                      </li>
                      <li>
                        {formatMessage(messages.notification.info.bullet2)}
                      </li>
                      <li>
                        {formatMessage(messages.notification.info.bullet3)}
                      </li>
                    </ul>
                  </NotificationContentText>
                </Notification>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {complete ? (
            itineraryStatus !== 'Rejected' && (
              <Button color="primary" onClick={this.handleClose}>
                {viewItinerary}
              </Button>
            )
          ) : (
            <Button
              color="primary"
              onClick={this.handleAccept}
              disabled={
                loading ||
                (itineraryStatus === 'Rejected' && userNote.length < 3)
              }
            >
              {accept}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
  private handleClose = () =>
    this.props.handleClose(this.props.itineraryStatus, this.props.complete);

  private handleChangeNote = (event: ChangeEvent<any>) =>
    this.props.setUserNote(event.target.value);

  private handleAccept = () => this.props.handleAccept();
}

// styled
const styles = ({ spacing: { unit: spacing } }: Theme) =>
  createStyles({
    textInput: {
      marginBottom: spacing * 2,
    },
    italic: {
      fontStyle: 'italic',
    },
  });

const mapStateToProps = (state: AppState): StateProps => ({
  open: state.confirmDialog.open,
  userNote: state.confirmDialog.note,
  itineraryStatus: state.confirmDialog.itineraryStatus,
  loading: state.confirmDialog.loading,
  complete: state.confirmDialog.showEmailSent,
  error: state.confirmDialog.showError,
  arrangerNote: getTravelArrangerNotes(state),
  arrangerName: getTravelArrangerName(state),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  handleClose(itineraryStatus: ItineraryStatus, actionComplete: boolean) {
    dispatch(toggleConfirmDialog(false, itineraryStatus));
    if (actionComplete) dispatch(requestItinerary());
  },
  setUserNote(note: string) {
    dispatch(updateUserNote(note));
  },
  handleAccept() {
    dispatch(requestItineraryChanges());
  },
});

const enhance = compose<ConfirmDialogProps, OwnProps>(
  pure,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withMobileDialog({ breakpoint: 'sm' }),
  withStyles(styles),
  injectIntl,
);

/**
 * Dialog for 'Request changes" and "Accept itinerary"
 */
export const ConfirmDialog = enhance(ConfirmDialogBase);

ConfirmDialog.displayName = 'ConfirmDialog';
